import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwal } from '@kiper/hooks';
import { WORKSTATION_AUTH_STATUS } from '../../../../services/workstationAuth';
import { isMfaEnrollmentSessionExpired } from '../mfaEnrollmentErrors';
import { clearMfaFlow } from '../mfaFlowStorage';
import { steps } from '../steps';

// Roteia o passo seguinte quando a estação não está aprovada (Pending ou
// Rejected) a partir do que o sign-in barrado devolveu. Condomínio é
// avaliado ANTES de Parceiro: o backend nunca preenche os dois ao mesmo
// tempo — porteiro com vínculo de condomínio (fase 2) recebe `condominiums`
// preenchida e `partners` vazia; quem não tem condomínio segue no fluxo de
// Parceiro de sempre. Em cada lista, mais de uma opção → a pessoa escolhe
// (dropdown); exatamente uma → o contexto é automático; nenhuma (só possível
// em Parceiro, em Rejected) → orienta a procurar o administrador (nunca
// dropdown vazio, nunca CTA de solicitar).
//
// A lista escolhida (condominiums ou partners) é guardada no Formik tanto no
// Rejected quanto no Pending: sem isso, quem reabre o Pending de uma sessão
// anterior e clica "solicitar novamente" reenvia o cadastro com o contexto
// nulo quando há mais de uma opção elegível.
function routeWorkstationOutcome({
  status,
  workstationAuthorization,
  helpers,
  setCurrentStep,
}) {
  const condominiums = workstationAuthorization?.condominiums || [];
  const partners = workstationAuthorization?.partners || [];

  if (status === WORKSTATION_AUTH_STATUS.PENDING) {
    if (condominiums.length > 1) {
      helpers.setFieldValue('condominiums', condominiums);
    } else if (condominiums.length === 1) {
      helpers.setFieldValue('condominiumContextId', condominiums[0].id);
    } else if (partners.length > 1) {
      helpers.setFieldValue('partners', partners);
    } else if (partners.length === 1) {
      helpers.setFieldValue('partnerContextId', partners[0].partnerContextId);
    }
    setCurrentStep(steps.workstationPending);
    return;
  }

  if (status === WORKSTATION_AUTH_STATUS.REJECTED) {
    if (condominiums.length > 1) {
      helpers.setFieldValue('condominiums', condominiums);
      setCurrentStep(steps.workstationCondominiumSelect);
    } else if (condominiums.length === 1) {
      helpers.setFieldValue('condominiumContextId', condominiums[0].id);
      setCurrentStep(steps.workstationRegister);
    } else if (partners.length > 1) {
      helpers.setFieldValue('partners', partners);
      setCurrentStep(steps.workstationPartnerSelect);
    } else if (partners.length === 1) {
      helpers.setFieldValue('partnerContextId', partners[0].partnerContextId);
      setCurrentStep(steps.workstationRegister);
    } else {
      setCurrentStep(steps.workstationNoPartner);
    }
  }
}

/**
 * Handlers do login. Recebem `(values, helpers)` do Formik.
 *
 * MFA obrigatória: quando o BE exige MFA e a pessoa ainda não tem TOTP, o
 * signIn devolve `mfaEnrollmentSession` no lugar dos tokens. Essa sessão
 * guardada nos values é o discriminador do modo obrigatório no fluxo inteiro
 * de cadastro (LoginMfaInvite, LoginMfaInstallApp e confirmEnrollment). Os
 * tokens só chegam no verifyMfaEnrollment; se a sessão expirar (TTL de 5min
 * no BE), a pessoa volta para a senha e refaz o login.
 */
export default function useLoginActions({
  auth,
  history,
  navigateToApp,
  setCurrentStep,
  workstationAuthorizationRequired,
  setWorkstationAuthorizationRequired,
  resolveWorkstationAuthorizationRequired,
}) {
  const [t] = useTranslation('login');
  const { toast } = useSwal();

  const emailValidation = useCallback(
    async ({ email }, helpers) => {
      const {
        exists,
        workstationAuthorizationRequired: flag,
      } = await resolveWorkstationAuthorizationRequired(email);
      helpers.setFieldError('email', !exists && 'userDoesntExists');
      if (exists) {
        setWorkstationAuthorizationRequired(!!flag);
        setCurrentStep(steps.password);
        history.push({
          pathname: '/',
          search: `?email=${encodeURIComponent(email)}`,
        });
      }
    },
    [
      resolveWorkstationAuthorizationRequired,
      setWorkstationAuthorizationRequired,
      setCurrentStep,
      history,
    ],
  );

  const signIn = useCallback(
    async ({ email: username, password }, helpers) => {
      try {
        const result = await auth.login({
          username,
          password,
          workstationAuthorizationRequired,
        });
        const signInResult = result?.signIn;
        // Senha expirada → renovação.
        if (signInResult?.passwordExpired) {
          history.push({
            pathname: '/renew-password',
            search: `?email=${encodeURIComponent(username)}`,
          });
          return;
        }
        // Tem 2FA → desafio.
        if (signInResult?.mfaRequired) {
          helpers.setFieldValue('mfaSession', signInResult.session);
          setCurrentStep(steps.mfaChallenge);
          return;
        }
        // MFA obrigatória e sem TOTP → cadastro forçado (ver doc do hook).
        if (signInResult?.mfaEnrollmentSession) {
          helpers.setFieldValue(
            'mfaEnrollmentSession',
            signInResult.mfaEnrollmentSession,
          );
          helpers.setFieldValue('mfaUser', signInResult.user);
          setCurrentStep(steps.mfaEnrollInvite);
          return;
        }
        // Sem 2FA → convite de cadastro (opcional).
        if (signInResult?.enableMfaConfiguration) {
          helpers.setFieldValue('mfaUser', signInResult.user);
          setCurrentStep(steps.mfaEnrollInvite);
          return;
        }
        routeWorkstationOutcome({
          status: result?.status,
          workstationAuthorization: signInResult?.workstationAuthorization,
          helpers,
          setCurrentStep,
        });
      } catch (error) {
        helpers.setFieldError('password', 'invalid');
      }
    },
    [auth, workstationAuthorizationRequired, history, setCurrentStep],
  );

  // Valida o código do desafio. Quem está sujeito ao portão de workstation e
  // tem TOTP pré-existente cai aqui direto (o signIn curto-circuita no
  // desafio antes do portão ser avaliado) — o BE avalia o portão junto da
  // validação do código e devolve `workstationAuthorization` quando a estação
  // não estiver aprovada, para cair na mesma máquina de estados do signIn
  // barrado em vez de mostrar "código inválido" com o código certo.
  //
  // `passwordExpired` também pode chegar aqui (não só no signIn): o BE checa
  // senha expirada depois do desafio TOTP, então código certo + senha vencida
  // responde sem tokens e com essa flag.
  const respondChallenge = useCallback(
    async ({ mfaSession, code, email }, helpers) => {
      try {
        const { status, signIn: signInResult } = await auth.respondMfa({
          session: mfaSession,
          code,
          username: email,
        });
        if (signInResult?.accessToken) {
          navigateToApp(signInResult.user);
          return;
        }
        if (signInResult?.passwordExpired) {
          history.push({
            pathname: '/renew-password',
            search: `?email=${encodeURIComponent(email)}`,
          });
          return;
        }
        if (
          status === WORKSTATION_AUTH_STATUS.PENDING ||
          status === WORKSTATION_AUTH_STATUS.REJECTED
        ) {
          routeWorkstationOutcome({
            status,
            workstationAuthorization: signInResult?.workstationAuthorization,
            helpers,
            setCurrentStep,
          });
          return;
        }
        // Sem status de portão (usuário fora do piloto) → código realmente
        // inválido, comportamento inalterado.
        helpers.setFieldError('code', 'invalid');
      } catch (error) {
        helpers.setFieldError('code', 'invalid');
      }
    },
    [auth, navigateToApp, history, setCurrentStep],
  );

  // Ativa o TOTP e loga direto: no modo obrigatório os cookies são gravados
  // pelo verifyMfaEnrollment (provider); no opcional, os do signIn já valem.
  const confirmEnrollment = useCallback(
    async ({ code, mfaUser, mfaEnrollmentSession }, helpers) => {
      try {
        if (mfaEnrollmentSession) {
          // mfaUser vem null nesse modo (o signIn não teve accessToken para
          // resolvê-lo) — o user real vem na resposta do verify.
          const result = await auth.verifyMfaEnrollment({
            enrollmentSession: mfaEnrollmentSession,
            code,
          });
          navigateToApp(result?.user || mfaUser);
          return;
        }
        await auth.verifyToken({ code });
        navigateToApp(mfaUser);
      } catch (error) {
        // Sessão expirada → volta para a senha; qualquer outro erro (shape
        // desconhecido incluso) → código inválido, igual ao fluxo atual.
        if (mfaEnrollmentSession && isMfaEnrollmentSessionExpired(error)) {
          helpers.setFieldValue('mfaEnrollmentSession', null);
          clearMfaFlow();
          toast.fire({ title: t('two-step.session-expired'), icon: 'error' });
          setCurrentStep(steps.password);
          return;
        }
        helpers.setFieldError('code', 'invalid');
      }
    },
    [auth, navigateToApp, setCurrentStep, t, toast],
  );

  const forgot = useCallback(
    ({ email: username }, helpers) =>
      auth.forgot({ username }).catch(() => helpers.setFieldError('password')),
    [auth],
  );

  return {
    emailValidation,
    signIn,
    respondChallenge,
    confirmEnrollment,
    forgot,
  };
}
